"use client";

import { useReducedMotion } from "framer-motion";
import { useEffect, useReducer } from "react";

import {
  createShuffledRotationOrder,
  getLogoRotationDelayMs,
} from "./customers-grid-animation";
import type { CustomerLogoId, LogoSlotConfig } from "./customers-grid-catalog";

export interface RotationState {
  slots: LogoSlotConfig[];
  pool: CustomerLogoId[];
  rotationOrder: number[];
  orderIndex: number;
}

export function createInitialRotationState(
  initialSlots: LogoSlotConfig[],
  initialPool: CustomerLogoId[],
  random = Math.random
): RotationState {
  return {
    slots: initialSlots,
    pool: initialPool,
    rotationOrder: createShuffledRotationOrder(initialSlots.length, random),
    orderIndex: 0,
  };
}

export function rotateLogos(state: RotationState): RotationState {
  if (state.pool.length === 0 || state.rotationOrder.length === 0) {
    return state;
  }

  const index = state.rotationOrder[state.orderIndex]!;
  const nextId = state.pool[state.pool.length - 1];
  const currentSlot = state.slots[index];
  if (!nextId || !currentSlot) return state;

  const outgoingId = currentSlot.id;
  const newSlots = [...state.slots];
  newSlots[index] = {
    ...currentSlot,
    id: nextId,
  };

  return {
    slots: newSlots,
    pool: [...state.pool.slice(0, -1), outgoingId],
    rotationOrder: state.rotationOrder,
    orderIndex: (state.orderIndex + 1) % state.rotationOrder.length,
  };
}

export function useCustomersGridLogoRotation(
  initialSlots: LogoSlotConfig[],
  initialPool: CustomerLogoId[],
  enabled: boolean,
  /** Freeze rotation in place (e.g. while a card is expanded) without resetting the displayed logos. */
  paused = false
) {
  const prefersReducedMotion = useReducedMotion();
  const active = enabled && !prefersReducedMotion;

  const [state, dispatch] = useReducer(rotateLogos, undefined, () =>
    createInitialRotationState(initialSlots, initialPool)
  );

  useEffect(() => {
    if (!active || paused) return;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        dispatch();
        scheduleNext();
      }, getLogoRotationDelayMs());
    };

    scheduleNext();
    return () => clearTimeout(timeoutId);
  }, [active, paused]);

  return active ? state.slots : initialSlots;
}
